import { useState, useRef, useCallback } from 'react';
import { msalInstance } from '../../../index';

const API_SCOPE = `${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/InternalApi`;
export const BASE_URL = (process.env.REACT_APP_MedNec_API_BASE_URL || '').replace(/\/+$/, '');

// File upload limits
const MAX_FILE_SIZE_BYTES = 31_457_280; // 30MB
const MAX_FILE_SIZE_MB = 30;

interface SseEvent {
  type: string;
  conversationId?: string;
  delta?: string;
  inputTokens?: number;
  outputTokens?: number;
  message?: string;
}

interface UsePolicyChatStreamReturn {
  sendMessage: (message: string) => Promise<void>;
  sendMessageWithDocument: (message: string, files: File[]) => Promise<void>;
  resetChat: () => void;
  isStreaming: boolean;
  streamingText: string;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  error: string | null;
  lastTokenUsage: { input: number; output: number } | null;
}

export async function getAuthToken(): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) return null;
    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes: [API_SCOPE],
    });
    return response.accessToken;
  } catch {
    return null;
  }
}

export function usePolicyChatStream(): UsePolicyChatStreamReturn {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastTokenUsage, setLastTokenUsage] = useState<{ input: number; output: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleSseEvent = useCallback((event: SseEvent): void => {
    if (event.type === 'conversation_id' && event.conversationId) {
      setConversationId(event.conversationId);
    } else if (event.type === 'delta' && event.delta) {
      setStreamingText(prev => prev + event.delta);
    } else if (event.type === 'done') {
      setLastTokenUsage({
        input: event.inputTokens ?? 0,
        output: event.outputTokens ?? 0,
      });
    } else if (event.type === 'error') {
      setError(event.message ?? 'Unknown error');
    }
  }, []);

  const processSseLines = useCallback((lines: string[]): boolean => {
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (payload === '[DONE]') return true;
      try {
        handleSseEvent(JSON.parse(payload));
      } catch {
        // Skip malformed JSON
      }
    }
    return false;
  }, [handleSseEvent]);

  const processStream = useCallback(async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      if (processSseLines(lines)) return;
    }

    // Process any remaining data in the buffer after stream ends
    if (buffer.trim()) {
      processSseLines(buffer.split('\n'));
    }
  }, [processSseLines]);

  const sendMessage = useCallback(async (message: string) => {
    setIsStreaming(true);
    setStreamingText('');
    setError(null);
    setLastTokenUsage(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getAuthToken();
      const res = await fetch(`${BASE_URL}/PolicyChat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message, conversationId }),
        signal: controller.signal,
      });

      if (!res.ok) {
        setError(`Request failed: ${res.status}`);
        return;
      }

      await processStream(res);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Stream failed');
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [conversationId, processStream]);

  const sendMessageWithDocument = useCallback(async (message: string, files: File[]) => {
    // Validate individual file sizes
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`File "${file.name}" exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB`);
        return;
      }
    }

    // Validate aggregate size
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_FILE_SIZE_BYTES) {
      setError(`Total file size exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    setIsStreaming(true);
    setStreamingText('');
    setError(null);
    setLastTokenUsage(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('message', message);
      for (const file of files) {
        formData.append('files', file);
      }
      if (conversationId) formData.append('conversationId', conversationId);

      const res = await fetch(`${BASE_URL}/PolicyChat/stream/with-document`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
        signal: controller.signal,
      });

      if (!res.ok) {
        setError(`Request failed: ${res.status}`);
        return;
      }

      await processStream(res);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Stream failed');
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [conversationId, processStream]);

  const resetChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setConversationId(null);
    setStreamingText('');
    setError(null);
    setLastTokenUsage(null);
  }, []);

  return {
    sendMessage,
    sendMessageWithDocument,
    resetChat,
    isStreaming,
    streamingText,
    conversationId,
    setConversationId,
    error,
    lastTokenUsage,
  };
}
